import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCustomers } from '../context/CustomerContext';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import VehicleDescriptionGenerator from '../components/AIFeatures/VehicleDescriptionGenerator';
import CompetitiveComparison from '../components/AIFeatures/CompetitiveComparison';

const { FiArrowLeft, FiEdit3, FiTrash2, FiCar, FiCalendar, FiDollarSign, FiTag, FiLoader, FiCpu } = FiIcons;

function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useCustomers();
  const { vehicles, loading } = state;
  const vehicle = vehicles.find(v => v.id === id);
  const [showAITools, setShowAITools] = useState(false);

  if (!vehicle && !loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Vehicle not found</p>
        <button
          onClick={() => navigate('/inventory')}
          className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
        >
          Back to Inventory
        </button>
      </div>
    );
  }

  if (loading || !vehicle) {
    return (
      <div className="text-center py-12">
        <SafeIcon icon={FiLoader} className="w-8 h-8 text-primary-600 mx-auto mb-4 animate-spin" />
        <p className="text-gray-500">Loading vehicle details...</p>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-100 text-green-700';
      case 'sold':
        return 'bg-purple-100 text-purple-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'maintenance':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/inventory')}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
          Back to Inventory
        </button>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAITools(!showAITools)}
            className={`p-2 rounded-lg transition-colors ${
              showAITools ? 'bg-primary-50 text-primary-600' : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
            }`}
          >
            <SafeIcon icon={FiCpu} className="w-4 h-4" />
          </button>
          <button className="p-2 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors">
            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
          </button>
          <button className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Vehicle Info */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            {/* Vehicle Image */}
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 relative">
              {vehicle.imageUrl ? (
                <img
                  src={vehicle.imageUrl}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                  className="w-full h-64 object-cover"
                />
              ) : (
                <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                  <SafeIcon icon={FiCar} className="w-20 h-20 text-gray-400" />
                </div>
              )}
              <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(vehicle.status)}`}>
                  {vehicle.status}
                </span>
              </div>
            </div>

            {/* Vehicle Details */}
            <div className="p-6">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {vehicle.year} {vehicle.make} {vehicle.model}
              </h1>
              <div className="flex items-center space-x-4 mb-4">
                <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium">
                  {vehicle.type}
                </span>
                <span className="text-2xl font-bold text-green-600">${vehicle.price?.toLocaleString()}</span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiTag} className="w-5 h-5 mr-3" />
                  <span>Color: {vehicle.color}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiCar} className="w-5 h-5 mr-3" />
                  <span>Mileage: {vehicle.mileage?.toLocaleString()} miles</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-3" />
                  <span>Year: {vehicle.year}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <SafeIcon icon={FiDollarSign} className="w-5 h-5 mr-3" />
                  <span>Price: ${vehicle.price?.toLocaleString()}</span>
                </div>
              </div>

              {vehicle.description && (
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="text-gray-700">{vehicle.description}</p>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-gray-900 mb-2">Vehicle Information</h3>
                <div className="grid grid-cols-2 gap-y-2">
                  <div className="text-sm">
                    <span className="text-gray-500">VIN:</span>
                  </div>
                  <div className="text-sm font-medium">{vehicle.vin}</div>
                  <div className="text-sm">
                    <span className="text-gray-500">Make:</span>
                  </div>
                  <div className="text-sm font-medium">{vehicle.make}</div>
                  <div className="text-sm">
                    <span className="text-gray-500">Model:</span>
                  </div>
                  <div className="text-sm font-medium">{vehicle.model}</div>
                  <div className="text-sm">
                    <span className="text-gray-500">Type:</span>
                  </div>
                  <div className="text-sm font-medium">{vehicle.type}</div>
                  <div className="text-sm">
                    <span className="text-gray-500">Status:</span>
                  </div>
                  <div className="text-sm font-medium">{vehicle.status}</div>
                  <div className="text-sm">
                    <span className="text-gray-500">Added:</span>
                  </div>
                  <div className="text-sm font-medium">{format(new Date(vehicle.createdAt), 'MMM d, yyyy')}</div>
                </div>
              </div>

              <div className="flex space-x-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex-1"
                >
                  Mark as Sold
                </motion.button>
                <Link to="/sales">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
                  >
                    Record Sale
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-2">
          {/* AI Tools */}
          {showAITools && (
            <div className="space-y-6">
              <VehicleDescriptionGenerator vehicle={vehicle} />
              <CompetitiveComparison vehicle={vehicle} />
            </div>
          )}

          {/* Additional Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Features</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                <span className="text-gray-700">Premium Audio System</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                <span className="text-gray-700">Leather Interior</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                <span className="text-gray-700">Navigation System</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                <span className="text-gray-700">Backup Camera</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                <span className="text-gray-700">Bluetooth Connectivity</span>
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 bg-primary-600 rounded-full mr-2"></div>
                <span className="text-gray-700">Keyless Entry</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Similar Vehicles</h3>
            <div className="space-y-4">
              {vehicles
                .filter(v => v.id !== vehicle.id && v.type === vehicle.type && v.status === 'available')
                .slice(0, 3)
                .map(similarVehicle => (
                  <div
                    key={similarVehicle.id}
                    className="flex items-center space-x-4 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0">
                      {similarVehicle.imageUrl ? (
                        <img
                          src={similarVehicle.imageUrl}
                          alt={`${similarVehicle.year} ${similarVehicle.make} ${similarVehicle.model}`}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-gray-200 flex items-center justify-center rounded-lg">
                          <SafeIcon icon={FiCar} className="w-8 h-8 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {similarVehicle.year} {similarVehicle.make} {similarVehicle.model}
                      </p>
                      <p className="text-sm text-gray-500">${similarVehicle.price?.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default VehicleDetail;